<script lang="ts" setup>
import { ref, defineEmits, computed, watch, ComputedRef } from 'vue';
import { sessionFlag } from '@/api/session';
import { useUserStore } from '@/stores/user';
import { useCamStore } from '@/stores/cam';
import { usePerformerStore } from '@/stores/performer';
import { getUnixTime } from 'date-fns';
import type { Flag } from '@/ontology/flag';

const emit = defineEmits(['update:visible']);
const flagRemarks = ref('');
const flagSubject = ref('select');
//const isSubmitted = ref(false);
const maxCharacters = 255;

const user = useUserStore();
const cam = useCamStore();
const performerStore = usePerformerStore();
const performer = computed(() => performerStore.getById(cam.performer));
const subjects = ['select', 'age', 'advertising', 'abusive', 'intoxicated', 'payments', 'sleeping', 'gender', 'other'];

const handleClose = () => {    
    emit('update:visible', false);
};

const isSubmitted = computed(() => {    
    return !user.canSendReport();
})

const handleSubmit = async () => {

    if (flagSubject.value === 'select') {
        return;
    }

    if (flagRemarks.value.trim() && !isSubmitted.value) {


        const flaggerId = user.account.id;
        const flaggerName = user.account.username;

        if (flaggerId !== undefined && flaggerName !== undefined) {

            const flagData: Flag = {
                flaggerId: flaggerId,
                flaggerName: flaggerName,
                flaggerType: 'CLIENT',
                flaggeeId: cam.performer,
                flaggeeName: performer.value?.nickname || '',
                flaggeeType: 'PERFORMER',
                type: flagSubject.value,
                severity: 'LOW',
                snapshot: getUnixTime(new Date()),
                sessionUuidName: cam.playStream?.id || '',
                remarks: `${flagRemarks.value.trim()}` 
            };

            const response = await sessionFlag(flagData);

            if (response.error) {
                console.error('Error submitting flag:', response.error);                
            } else {
                flagRemarks.value = '';
                user.updateReportSend();
            }
        }
    }
};
const isDisabled: ComputedRef<boolean> = computed((): boolean => {
    return !flagRemarks.value || isSubmitted.value || flagSubject.value === 'select';
})

watch(flagRemarks, (newRemarks) => {
    if (newRemarks.length > maxCharacters) {
        flagRemarks.value = newRemarks.slice(0, maxCharacters);
    }
});
</script>

<template>
    <div class="flag-form">
        <div class="flag-form__content" @click.stop>
            <div v-if="!isSubmitted">
                <div class="flag-form__title">{{ $t('profile.session.flag.title') }}</div>
                <div class="flag-form__desc" v-html="$t('profile.session.flag.desc')"></div>
                <form @submit.prevent="handleSubmit">
                    <div class="flag-form__formgroup">
                        <label for="flagSubject">
                            {{  $t(`profile.session.flag.subject`) }}
                        </label>
                        <select id="flagSubject" v-model="flagSubject" class="flag-form__select">
                            <option v-for="subject in subjects" :key="subject" :value="subject" >
                                {{  $t(`profile.session.flag.subjects.${subject}`) }}
                            </option>
                        </select>
                    </div>
                    <div class="flag-form__formgroup">
                        <textarea v-model="flagRemarks" class="flag-form__textarea"
                            :placeholder="$t('profile.session.flag.placeholder')" :maxlength="maxCharacters"></textarea>
                        <div class="flag-form__count">{{ flagRemarks.length }} / {{ maxCharacters }}</div>
                    </div>
                    <div class="flag-form__formgroup">
                        <button type="submit" class="btn btn-full btn-small"  :class="isDisabled ? 'disabled' : '' " :disabled="isDisabled">{{
                            $t('profile.session.flag.submit') }}</button>
                        <button type="button" class="btn btn-full btn-small btn-black" @click="handleClose()">{{
                            $t('profile.session.flag.cancel') }}</button>
                    </div>
                </form>
            </div>
            <div v-else>
                <div class="flag-form__title">{{ $t('profile.session.flag.thanks') }}</div>
                <p class="flag-form__subtitle">{{ $t('profile.session.flag.handling') }}</p>
                <button  class="btn btn-full btn-small" @click="handleClose()">{{ $t('profile.session.flag.confirm') }}</button>
            </div>
        </div>
    </div>
</template>

<style lang="scss">
@import '@/styles/_mixins.scss';

.flag-form {
    position: absolute;
    top: -174px;
    z-index: 6;
    left: calc($videochat-controls-width + 15px);
    font-family: $font-regular;
    text-align: left;
    font-weight: 400;
    @include border-radius(10px);
    width: $videochat-controls-dropdown-width;
    background: $videochat-controls-dropdown-item-bgcolor;
    overflow-x: hidden;
    user-select: none;

     
    &__content {
        margin: 1rem;
    }

    &__title {
        line-height: 110%;
        @include rem(font-size, 16px);
        @include rem(padding, 0px 0px 5px 0px);
    }

    &__subtitle {
        color: $pallete-1;
        line-height: 130%;
        @include rem(font-size, 16px);
        font-style: italic;
    }

    &__formgroup {
        position: relative;
        display: flex;
        flex-direction: column;
        @include rem(padding, 5px 0px);
        label {
            @include rem(font-size, 13px);
            line-height: 280%;
        }
    }

    &__desc {
        color: $pallete-2;
        line-height: 120%;
        @include rem(font-size, 10px);
        @include rem(padding, 10px 0px 0px 0px);
    }

    &__textarea, &__select {
        @include rem(padding,10px 5px);
        @include border-radius(5px);
        @include rem(font-size, 12px);
        background: $pallete-0;
        color: $pallete-1;
        font-family: $font-regular;
        font-style: italic;
    }

    &__textarea {
        @include rem(height, 100px);
        font-style: italic;
        resize: none;
        &::placeholder {
            color: $pallete-1;
            font-family: $font-regular;
            font-style: italic;
        }
    }

    .disabled {
        opacity: 0.6;
        color: $btn-regular-color !important;
        background: $btn-regular-hcolor;
    }


    &__count {
        color: $pallete-1;
        line-height: normal;
        text-align: right;
        @include rem(font-size, 8px);
    }
}
</style>
